/**
 * Theme scoping — puts `data-theme` on <html> for the routes that ask for it.
 *
 * The tenant portal runs on the `marine` colour set while the manager side stays
 * on the default indigo. Both live in `design-tokens.scss`; this service decides
 * which one is active by mirroring the route tree onto a single root attribute.
 *
 * Why the ROOT and not the tenant shell — this is the whole point of the file:
 * Material dialogs and toasts render inside `.cdk-overlay-container`, a sibling
 * of the app root. A theme scoped to `<app-tenant-shell>` would style the page
 * and leave every modal, confirm and toast on the other palette. Setting it on
 * `document.documentElement` covers both trees at once, and mirrors what the
 * visual reference does (`:root[data-theme="…"]`).
 *
 * Driven by the router rather than a component lifecycle for the same reason: an
 * overlay can outlive the component that opened it, and the public invitation
 * landing is tenant-facing but lives outside the tenant shell.
 *
 * A route opts in declaratively:
 *
 *   { path: 'tenant', data: { theme: 'tenant' }, … }
 *
 * The value is inherited by children unless one overrides it, so the flag is set
 * once on the parent. Absence of the attribute is the default theme — never
 * `data-theme="default"`.
 */
import { DestroyRef, DOCUMENT, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/** Attribute name on <html>. Matches the visual reference's own switcher. */
export const THEME_ATTRIBUTE = 'data-theme';

/** Route `data` key a route sets to claim a theme. */
export const THEME_DATA_KEY = 'theme';

/**
 * Deepest `data.theme` in an activated route branch, or null when no route on
 * the branch claims one. Exported so the resolution can be tested without a
 * router: it is the part that actually decides.
 */
export function resolveRouteTheme(root: ActivatedRouteSnapshot | null): string | null {
  let theme: string | null = null;
  let node = root;

  while (node) {
    const claimed = node.data?.[THEME_DATA_KEY];
    if (typeof claimed === 'string' && claimed.length > 0) {
      theme = claimed;
    }
    node = node.firstChild;
  }

  return theme;
}

@Injectable({ providedIn: 'root' })
export class ThemeScopeService {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;

  /**
   * Begin mirroring the route tree onto <html>. Called once from AppComponent.
   * Idempotent: a second call is a no-op rather than a second subscription.
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.apply(resolveRouteTheme(this.router.routerState.snapshot.root));

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.apply(resolveRouteTheme(this.router.routerState.snapshot.root));
      });
  }

  private apply(theme: string | null): void {
    const root = this.document.documentElement;
    if (theme) {
      root.setAttribute(THEME_ATTRIBUTE, theme);
    } else {
      root.removeAttribute(THEME_ATTRIBUTE);
    }
  }
}
